import { EllipsisMenuButton, Menu, MenuContent, MenuTrigger, toaster } from '@optiaxiom/react';
import { IconLink } from '@optimizely/axiom-icons';

import { MAX_EVALUATION_SAMPLES } from '@frontend/features/agents/constants';
import {
  useCreateEvaluationSample,
  useGetEvaluationSamples,
} from '@frontend/features/agents/hooks/useEvaluationSamples';
import { getSpecializedAgentExecution } from '@frontend/features/agents/services/specialized-agent';
import { useInstanceId } from '@frontend/hooks';
import { SpecializedAgentExecution } from '@frontend/types';

type ActionsCellProps = {
  execution: SpecializedAgentExecution;
};

export const ActionsCell = ({ execution }: ActionsCellProps) => {
  const { instanceId } = useInstanceId();
  const createSampleMutation = useCreateEvaluationSample(instanceId || '', execution.agent_id);
  const { data: existingSamples = [] } = useGetEvaluationSamples(
    instanceId || '',
    execution.agent_id,
    !!instanceId && !!execution.agent_id
  );

  const handleLinkAsOutputEval = async () => {
    if (!instanceId) {
      toaster.create('Instance ID not found', { type: 'danger' });
      return;
    }

    // Check if limit of evaluation samples is reached
    if (existingSamples.length >= MAX_EVALUATION_SAMPLES) {
      toaster.create(
        `Cannot link execution: Maximum of ${MAX_EVALUATION_SAMPLES} evaluation samples already exists. Please delete an existing sample to add a new one.`,
        { type: 'warning' }
      );
      return;
    }

    try {
      // Fetch full execution details to ensure we have the complete result
      const fullExecution = await getSpecializedAgentExecution(instanceId, execution.id)();

      // Validate execution has output
      if (!fullExecution.result?.response) {
        toaster.create('Cannot create evaluation sample: execution has no output', {
          type: 'danger',
        });
        return;
      }

      // Generate name from timestamp
      const timestamp = new Date(fullExecution.created_at);
      const formattedDate = timestamp.toLocaleString();

      // Create evaluation sample with execution_id
      await createSampleMutation.mutateAsync({
        name: `Execution ${formattedDate}`,
        output: fullExecution.result.response,
        input_parameters: fullExecution.parameters || undefined,
        description: `Linked from execution ${fullExecution.id}`,
        execution_id: fullExecution.id,
      });

      toaster.create('Output linked as evaluation sample', {
        type: 'success',
      });
    } catch (error) {
      // Handle duplicate execution error (HTTP 409)
      if (error && typeof error === 'object' && 'response' in error) {
        const httpError = error as { response?: { status?: number } };
        if (httpError.response?.status === 409) {
          toaster.create('This execution has already been linked as an evaluation sample', {
            type: 'warning',
          });
          return;
        }
      }

      toaster.create('Failed to create evaluation sample', {
        type: 'danger',
      });
    }
  };

  return (
    <Menu
      options={[
        {
          label: 'Link as Output Eval',
          intent: 'neutral',
          execute: handleLinkAsOutputEval,
          addon: <IconLink />,
          disabled: createSampleMutation.isPending,
        },
      ]}
    >
      <MenuTrigger asChild>
        <EllipsisMenuButton appearance="subtle" aria-label="Execution actions menu" />
      </MenuTrigger>
      <MenuContent />
    </Menu>
  );
};
